// Display preferences — small zustand store with localStorage persistence.
// Holds the fee-adjusted-odds toggle, optional maker rebate assumption,
// the user's default Value Dashboard
// landing pick (sport / market type / price mode), the order-ticket
// defaults (contracts qty + expiry mode), the order-cancel confirmation
// guard, per-surface view modes for positions / open-orders / trades, and
// bot-board view/sort defaults.
// Landing prefs are consumed at mount
// time so mid-session changes don't yank the active view; order ticket
// prefs are read live so changes apply on the next ticket render;
// view modes are read reactively so the on-page toggles flip
// the layout live.

import { create } from 'zustand';
import { SPORT_TABS } from '../views/ValueDashboard/types';
import type { Sport, MarketType, PriceMode, SlateRange } from '../views/ValueDashboard/types';

const STORAGE_KEY = 'galactus:displaySettings';

/** Market types the dashboard tabs actually expose — narrows MarketType
 *  to the subset users can choose as a landing default. */
export type DashboardMarketType = 'moneyline' | 'spread-total' | 'series';

/** Default expiry mode seeded into every new order ticket. Mirrors the
 *  three buttons in the panel's Expiry row. */
export type DefaultExpiryMode = 'gts' | 'gtc' | 'custom';

/** Card vs table layout for open-orders / trades surfaces. */
export type SurfaceViewMode = 'card' | 'table';

/** Positions also get the dense compact-list view requested by traders. */
export type PositionViewMode = SurfaceViewMode | 'compact' | 'mini';

/** Edge bot boards share the compact/card/table renderer. */
export type BotBoardViewMode = 'compact' | 'card' | 'table';

/**
 * #677 — how a ladder rung presents the trader's OWN resting orders.
 *
 * `compact` is the #650/#676 arrangement: the two orders nearest the front of
 * the queue inline, the rest behind a `+N` disclosure. `expanded` shows every
 * order at the level in one contained region.
 *
 * Presentation only, and bounded in BOTH values — this is not a switch between
 * "bounded" and "unbounded". The pre-#650 flex row that took the price column
 * is not one of the choices and must never become one; expanded is contained
 * by construction (fixed max width, wraps, scrolls). See RestingOrderGroup.
 */
export type RestingOrderDisplay = 'compact' | 'expanded';

/**
 * How many own resting orders a ladder rung draws inline in `compact` before
 * the rest move behind the `+N` disclosure.
 *
 * It lives HERE, beside the preference it parameterizes, because it is no
 * longer only the ladder's business: the Settings row and the status-bar
 * mirror both name this number in copy a trader reads ("Orders 2", "the two
 * nearest the front"). Hardcoding it in those strings meant turning the knob
 * would leave three surfaces quietly lying about what compact does.
 *
 * ⚠️ The VALUE is not a matter of taste, and the reasoning for it lives with
 * the measurements in `RestingOrderGroup`'s file header — the price column's
 * text walks from 500px to 27px as the count rises, and 2 is the widest that
 * still renders cleanly at the narrowest supported viewport. Read that before
 * changing this, and re-measure at 1470×956 rather than trusting the comment.
 */
export const INLINE_CHIP_LIMIT = 2;

/** Compact vs detailed presentation for the GameDetail market sections
 *  (Moneyline, Spread/Total, Game Lines). Both presentations cover the same
 *  market SET, but not necessarily the same rows on screen: the line-bearing
 *  sections' compact view renders one selected spread/total line with the
 *  rest behind a chip strip, while the line-less sections (Moneyline, BTTS,
 *  props, events) render compact outcome bands (#105). Distinct outcomes
 *  (moneyline teams, BTTS) stay one band each; but a Player-Props / Events
 *  value ladder — a player+stat's 1+/2+/3+, a team's runs — collapses its
 *  rungs into ONE band behind a rung selector (#141), since those differ only
 *  by a numeric threshold. See MoneylineSection / SpreadTotalSection /
 *  GameLinesSection. */
export type MarketSectionViewMode = 'compact' | 'detailed';

/** Sort options exposed by the bot fleet boards. */
export type BotBoardSortKey = 'front' | 'eta' | 'net' | 'errors';

/** Sortable columns on the Value Dashboard's game table. Mirrors the
 *  SortableTh `sortKey` strings in GameTable.tsx — keep in sync if a new
 *  sortable column lands there. */
export type DashboardSortKey = 'date' | 'team' | 'queue' | 'velocity';
export type DashboardSortDir = 'asc' | 'desc';

/** Sort options for the Positions page's Open Orders surface. */
export type PositionsOrderSortKey = 'default' | 'size' | 'queue' | 'velocity';
export type PositionsOrderSortDir = 'asc' | 'desc';

/** Persisted default sort for the dashboard. `key: null` means "unsorted"
 *  — the table renders games in whatever order the upstream data source
 *  supplies, which is the prior factory behavior we preserve as the default
 *  so traders aren't subjected to a sort they didn't ask for. */
export interface DashboardSortDefault {
  key: DashboardSortKey | null;
  dir: DashboardSortDir;
}

export interface PositionsOrderSortDefault {
  key: PositionsOrderSortKey;
  dir: PositionsOrderSortDir;
}

interface PersistedShape {
  feeAdjustedOdds: boolean;
  /** When Fees is on, suppress Kalshi quotes whose fee schedule is missing or
   *  unsupported instead of showing the raw fallback. Default OFF: preserve
   *  today's visible market set while the adjacent warning explains the gap. */
  hideUnknownFeePrices: boolean;
  /** When on, fold Kalshi's Market Maker Program rebate (50% pregame / 100%
   *  live reduction of the maker fee) into maker-perspective fee + odds
   *  displays. Only takes effect when the org is enrolled (AuthUser
   *  .mmRebateEnabled) and the Fees toggle is on. Replaces the retired
   *  notional-bps model. See [[project_kalshi_mm_rebate_model]]. */
  makerRebateEnabled: boolean;
  /** First sport the Value Dashboard lands on. */
  defaultSport: Sport;
  /** Which market-type tab opens first. */
  defaultMarketType: DashboardMarketType;
  /** Initial price mode (maker = best bid, taker = best ask, market = last). */
  defaultPriceMode: PriceMode;
  /** Price levels shown per side in the side-panel orderbooks (Exchange
   *  OrderBook maxLevels). Default 8 = the long-standing fan; Prof asked
   *  for a tighter "current ±3" view (Loom, Jun 12) — set 3. */
  bookDepthLevels: number;
  /** Initial expiry mode in the order ticket (GTS / GTC / Custom). */
  defaultExpiryMode: DefaultExpiryMode;
  /** Polymarket tickets get their own expiry default. GTS there maps to a
   *  CLOB GTD that Polymarket auto-cancels at event start — a maker whose
   *  Kalshi default is GTS would silently lose resting poly orders at first
   *  pitch, so poly defaults GTC independently. */
  defaultPolyExpiryMode: DefaultExpiryMode;
  /** The Polymarket US rail's own expiry default, on the same terms as the
   *  Polymarket one: GTC, so a maker's resting orders are not silently killed
   *  at event start by a GTS carried over from Kalshi. */
  defaultPolyUsExpiryMode: DefaultExpiryMode;
  /** Default trade size. NOT auto-populated on mount (that was a footgun
   *  — re-entering the panel could leave a phantom default in the Qty
   *  field). Instead used by the trade ticket as the value to fill the
   *  Qty input with when the user picks a price somewhere (orderbook
   *  level click, pill click, etc.) AND the Qty is currently blank.
   *  Also surfaced as the value of the "+ N" button next to the Qty
   *  label so the trader can apply it explicitly at any time. Integer
   *  ≥ 1. */
  defaultContracts: number;
  /** Quick-size presets rendered as prefill chips in the order ticket's Qty
   *  label row (both venues, Oscar overtake included). Fixed, user-set values
   *  — deliberately NOT auto-tracked from fired orders (product decision,
   *  2026-08-17). 0–3 whole contracts ≥ 1, deduped, order preserved as
   *  entered. Empty = no chip row. */
  quickSizes: number[];
  /** Oscar overtake: default blocker size, in contracts. null = follow
   *  defaultContracts. Same blank-Qty opportunistic-fill rules as
   *  defaultContracts (never stomps a typed value). Integer ≥ 1 or null. */
  defaultBlockerContracts: number | null;
  /** Oscar overtake: whether the "attach blocker" toggle starts ON when the
   *  trader enters overtake mode. */
  oscarBlockerDefaultOn: boolean;
  /** Layout for the Positions page's Open Positions surface. */
  positionViewMode: PositionViewMode;
  /** Layout for the Positions page's Open Orders surface. */
  orderViewMode: SurfaceViewMode;
  /** Layout for My Trades. */
  tradeViewMode: SurfaceViewMode;
  /** GameDetail Spread/Total section: compact chip rows (default) vs the
   *  detailed GameRows table. Presentation only — same markets either way. */
  spreadTotalViewMode: MarketSectionViewMode;
  /** GameDetail Game Lines section: detailed table (default) vs compact.
   *  ONE density preference for the whole section — the spread/total tabs
   *  render the compact chip rows, and the line-less tabs (Moneyline, BTTS,
   *  Player Props, Events) render compact outcome bands (#105), with
   *  Player-Props / Events value-ladder rungs collapsed behind a rung
   *  selector (#141). */
  gameLinesViewMode: MarketSectionViewMode;
  /** GameDetail Moneyline section: detailed table (default) vs compact
   *  outcome bands. Its own field because the Moneyline section sits on
   *  screen ALONGSIDE Spread/Total and Game Lines — surfaces visible at the
   *  same time get independent density (cf. myBotsViewMode / orgBotsViewMode),
   *  while tabs you switch between share one (cf. gameLinesViewMode). */
  moneylineViewMode: MarketSectionViewMode;
  /** Initial sort for the Value Dashboard's game table. Consumed at mount
   *  time only — header clicks during the session do not flow back into
   *  this setting, matching the other landing prefs above. Factory default
   *  is `{ key: null }` (unsorted) so traders see the prior data-natural
   *  order until they explicitly pick a default here. */
  defaultDashboardSort: DashboardSortDefault;
  /** Initial Open Orders sort for the Positions page. Consumed at mount
   *  time so ad hoc header/chip clicks remain session-local. */
  defaultPositionsOrderSort: PositionsOrderSortDefault;
  /** Default layout for the standalone /bots pages. */
  botFleetViewMode: BotBoardViewMode;
  /** Default layout for the Position Tracker's claimed bot section. */
  myBotsViewMode: BotBoardViewMode;
  /** Default layout for the Position Tracker's org bot section. */
  orgBotsViewMode: BotBoardViewMode;
  /** Default sort for the standalone /bots pages. */
  botFleetSort: BotBoardSortKey;
  /** Default sort for the Position Tracker's claimed bot section. */
  myBotsSort: BotBoardSortKey;
  /** Default sort for the Position Tracker's org bot section. */
  orgBotsSort: BotBoardSortKey;
  /** When true, direct cancel buttons open a confirmation step before
   *  calling the cancel API. Defaults on so the new one-click icons stay
   *  guarded unless the trader explicitly opts out. */
  confirmOrderCancel: boolean;
  /** Pull All and "Cancel this game": identify an order's game from its Kalshi
   *  ticker when the feed has not tied the market to a game yet (see
   *  gameOrderSelection's ticker fallback). Default off = fail closed. */
  gameCancelTickerFallback: boolean;
  /** P&L dollar visibility on the positions page. Defaults OFF per
   *  stakeholder preference (Joshua, Jun 2: "sucks to see a negative") —
   *  traders opt in via the eye toggle next to the Unrealized P&L tile. */
  showPnl: boolean;
  showRestingCloseCount: boolean;
  restingCloseCountOwnerId: string | null;
  /** Trade panel (side detail) scroll behavior. When true (DEFAULT), the order
   *  ticket stays pinned and only the order book scrolls within its own region —
   *  the classic desktop layout where BUY / Place Order are always in view. When
   *  false, the whole panel scrolls as one column so nothing is trapped on a
   *  short (laptop) viewport. */
  pinnedOrderTicket: boolean;
  /** OddsPapi "OP" comparison column + order-panel "O" row visibility.
   *  Default ON — but the surfaces additionally auto-hide when the current
   *  slate has zero OP data (old hydra / provider off), so this toggle only
   *  matters once the feed is actually live. Read reactively. */
  showOpOdds: boolean;
  /** Sports whose quote-empty Value Dashboard moneyline rows the user has
   *  explicitly chosen to suppress (#212, generalizing #192's tennis-only
   *  toggle). Empty = suppress nothing, which preserves the GBuff default
   *  experience; there is deliberately no separate hide-all flag that could
   *  contradict this list. Read by the per-sport toolbar picker shipped in
   *  #213. Stored deduped in SPORT_TABS order. */
  hiddenEmptySports: Sport[];
  /** #509 — opt-in "Kalshi markets only" row filter for the Value Dashboard
   *  moneyline board. Default OFF, so the current production experience is
   *  unchanged until a user turns it on. Deliberately a single boolean rather
   *  than a per-sport list: unlike `hiddenEmptySports` this asks one venue-level
   *  question ("is there an actionable Kalshi book here?") that does not vary by
   *  sport. The two controls are independent and neither writes the other. */
  kalshiMarketsOnly: boolean;
  /** #654 — opt-in "Saved only" mode for the Value Dashboard's College
   *  Football board. Default OFF, so the default CFB board stays the complete
   *  slate. Persisted rather than session-scoped because it is a working
   *  posture ("today I'm only trading my shortlist"), not a navigation choice
   *  — the board itself lives in the URL. Scoped by the CALLER, like
   *  kalshiMarketsOnly: off a CFB board the setting is preserved, never
   *  cleared, so switching sports and back doesn't silently lose it. */
  cfbSavedOnly: boolean;
  /** D-020 V2B — A/D hotkey layer master switch, default OFF ("dark until
   *  targeting validation"). Only meaningful when the trader also has
   *  lt2Access; without focus + armed one-click the listener no-ops anyway. */
  lt2HotkeysEnabled: boolean;
  /** V3 Spacebar Pull All (focused game). Default OFF — Spacebar stays an
   *  ordinary browser key until the trader deliberately opts the pull in;
   *  only meaningful with lt2Access. */
  lt2PullAllEnabled: boolean;
  /** #724 two-sided post (S). Default OFF — S stays an ordinary key, owns
   *  nothing in the remap panel, and the board's A/D/W/E/Space behaviour is
   *  byte-identical until the trader deliberately opts in. Only meaningful
   *  with lt2Access; each leg still needs the armed one-click + LT2 chain. */
  lt2TwoSidedEnabled: boolean;
  /** The trader who turned S on. The opt-in counts only for them (see
   *  `twoSidedOptedIn`); null = nobody, so S is off for everyone. */
  lt2TwoSidedOwnerId: string | null;
  /** #1067 — the Shift+S multiplier of the one-click size, per side. A whole
   *  number ≥2, or null = not configured, so Shift+S is unavailable. There is
   *  deliberately no default (Citadel named none). */
  lt2TwoSidedMultiplier: number | null;
  /** The trader who set the multiplier; it counts only for them (see
   *  `twoSidedMultiplier`), exactly like the S opt-in's owner. */
  lt2TwoSidedMultiplierOwnerId: string | null;
  /** D-020 V2A — reveal Polymarket-only / absent-Kalshi lines on the
   *  spread/total surfaces. Default OFF = those lines are hidden from the
   *  active execution view (packet's preferred treatment); ON shows them
   *  clearly disabled. Only applied when the trader has lt2Access. */
  spreadTotalShowPolyOnlyLines: boolean;
  /** D-020 V2C — nearest-three multi-line expanded view on spread/total
   *  rows. Default OFF (single-line two-sided ladder). */
  spreadTotalNearestLines: boolean;
  /** #677 — own-resting-order presentation on ladder rungs. Default
   *  `compact`, so shipping the preference changes nobody's ladder until they
   *  choose otherwise. Read reactively: the toggle flips live rungs. */
  restingOrderDisplay: RestingOrderDisplay;
  /** #712 — how wide the Value Dashboard's bounded date view is on the NFL and
   *  CFB game boards: one day, or the Tuesday–Monday window containing the
   *  selected date. Default `day`, so shipping the preference changes nobody's
   *  board until they opt in. Read reactively (the toolbar chip flips the board
   *  live) and inert on every other board — see `weeklySlate.ts`. */
  slateRange: SlateRange;
}

const DEFAULTS: PersistedShape = {
  feeAdjustedOdds: true,
  hideUnknownFeePrices: false,
  makerRebateEnabled: false,
  defaultSport: 'nba',
  defaultMarketType: 'moneyline',
  defaultPriceMode: 'maker',
  bookDepthLevels: 8,
  defaultExpiryMode: 'gts',
  defaultPolyExpiryMode: 'gtc',
  defaultPolyUsExpiryMode: 'gtc',
  defaultContracts: 1,
  quickSizes: [],
  defaultBlockerContracts: null,
  oscarBlockerDefaultOn: true,
  positionViewMode: 'compact',
  orderViewMode: 'card',
  tradeViewMode: 'card',
  spreadTotalViewMode: 'compact',
  gameLinesViewMode: 'detailed',
  // Detailed = what the Moneyline section renders today, so shipping the
  // toggle changes nobody's screen until they pick Compact (#105).
  moneylineViewMode: 'detailed',
  defaultDashboardSort: { key: null, dir: 'asc' },
  defaultPositionsOrderSort: { key: 'default', dir: 'asc' },
  botFleetViewMode: 'compact',
  myBotsViewMode: 'card',
  orgBotsViewMode: 'compact',
  botFleetSort: 'front',
  myBotsSort: 'front',
  orgBotsSort: 'front',
  confirmOrderCancel: true,
  gameCancelTickerFallback: false,
  showPnl: false,
  showRestingCloseCount: true,
  restingCloseCountOwnerId: null,
  pinnedOrderTicket: true,
  showOpOdds: true,
  hiddenEmptySports: [],
  kalshiMarketsOnly: false,
  cfbSavedOnly: false,
  lt2HotkeysEnabled: false,
  lt2PullAllEnabled: false,
  lt2TwoSidedEnabled: false,
  lt2TwoSidedOwnerId: null,
  lt2TwoSidedMultiplier: null,
  lt2TwoSidedMultiplierOwnerId: null,
  spreadTotalShowPolyOnlyLines: false,
  spreadTotalNearestLines: false,
  restingOrderDisplay: 'compact',
  slateRange: 'day',
};

// Derived from SPORT_TABS so the valid set can't drift from the dashboard's
// actual sports (the previous hand-rolled list had silently dropped 'nfl',
// which made a persisted defaultSport of 'nfl' fall back to 'nba').
const VALID_SPORTS: ReadonlySet<Sport> = new Set<Sport>(SPORT_TABS.map((t) => t.id));
const VALID_MARKET_TYPES: ReadonlySet<DashboardMarketType> = new Set<DashboardMarketType>([
  'moneyline', 'spread-total', 'series',
]);
const VALID_PRICE_MODES: ReadonlySet<PriceMode> = new Set<PriceMode>([
  'maker', 'taker', 'market',
]);
const VALID_EXPIRY_MODES: ReadonlySet<DefaultExpiryMode> = new Set<DefaultExpiryMode>([
  'gts', 'gtc', 'custom',
]);
const VALID_SURFACE_VIEW_MODES: ReadonlySet<SurfaceViewMode> = new Set<SurfaceViewMode>([
  'card', 'table',
]);
const VALID_POSITION_VIEW_MODES: ReadonlySet<PositionViewMode> = new Set<PositionViewMode>([
  'compact', 'table', 'card', 'mini',
]);
const VALID_RESTING_ORDER_DISPLAYS: ReadonlySet<RestingOrderDisplay> = new Set<RestingOrderDisplay>([
  'compact', 'expanded',
]);
const VALID_SLATE_RANGES: ReadonlySet<SlateRange> = new Set<SlateRange>([
  'day', 'week',
]);
const VALID_MARKET_SECTION_VIEW_MODES: ReadonlySet<MarketSectionViewMode> = new Set<MarketSectionViewMode>([
  'compact', 'detailed',
]);
const VALID_BOT_BOARD_VIEW_MODES: ReadonlySet<BotBoardViewMode> = new Set<BotBoardViewMode>([
  'compact', 'card', 'table',
]);
const VALID_DASHBOARD_SORT_KEYS: ReadonlySet<DashboardSortKey> = new Set<DashboardSortKey>([
  'date', 'team', 'queue', 'velocity',
]);
const VALID_POSITIONS_ORDER_SORT_KEYS: ReadonlySet<PositionsOrderSortKey> = new Set<PositionsOrderSortKey>([
  'default', 'size', 'queue', 'velocity',
]);
const VALID_BOT_BOARD_SORT_KEYS: ReadonlySet<BotBoardSortKey> = new Set<BotBoardSortKey>([
  'front', 'eta', 'net', 'errors',
]);
const VALID_SORT_DIRS: ReadonlySet<DashboardSortDir> = new Set<DashboardSortDir>([
  'asc', 'desc',
]);

/** Parse the persisted dashboard sort with the same defensive shape as the
 *  other validators — unknown key strings drop to factory default, missing
 *  fields fill in from factory. `key: null` is a valid persisted value
 *  (the "unsorted" sentinel). */
function readDashboardSort(raw: unknown): DashboardSortDefault {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS.defaultDashboardSort };
  const r = raw as { key?: unknown; dir?: unknown };
  const keyIsValid =
    r.key === null ||
    (typeof r.key === 'string' && VALID_DASHBOARD_SORT_KEYS.has(r.key as DashboardSortKey));
  const key = keyIsValid
    ? (r.key as DashboardSortKey | null)
    : DEFAULTS.defaultDashboardSort.key;
  const dir =
    typeof r.dir === 'string' && VALID_SORT_DIRS.has(r.dir as DashboardSortDir)
      ? (r.dir as DashboardSortDir)
      : DEFAULTS.defaultDashboardSort.dir;
  return { key, dir };
}

function readPositionsOrderSort(raw: unknown): PositionsOrderSortDefault {
  if (!raw || typeof raw !== 'object') return { ...DEFAULTS.defaultPositionsOrderSort };
  const r = raw as { key?: unknown; dir?: unknown };
  const validatedKey =
    typeof r.key === 'string' && VALID_POSITIONS_ORDER_SORT_KEYS.has(r.key as PositionsOrderSortKey)
      ? (r.key as PositionsOrderSortKey)
      : DEFAULTS.defaultPositionsOrderSort.key;
  // A saved DEFAULT must be a STABLE key. 'queue'/'velocity' re-order on every
  // market tick, so auto-loading into one made the positions view "spazz". Coerce
  // a persisted live default to the stable game-start order; traders can still pick
  // queue/velocity per-session (PositionTracker throttles those now).
  const key: PositionsOrderSortKey =
    validatedKey === 'queue' || validatedKey === 'velocity' ? 'default' : validatedKey;
  const dir =
    typeof r.dir === 'string' && VALID_SORT_DIRS.has(r.dir as PositionsOrderSortDir)
      ? (r.dir as PositionsOrderSortDir)
      : DEFAULTS.defaultPositionsOrderSort.dir;
  return { key, dir };
}

function readSurfaceViewMode(raw: unknown, fallback: SurfaceViewMode): SurfaceViewMode {
  return typeof raw === 'string' && VALID_SURFACE_VIEW_MODES.has(raw as SurfaceViewMode)
    ? (raw as SurfaceViewMode)
    : fallback;
}

function readPositionViewMode(raw: unknown, fallback: PositionViewMode): PositionViewMode {
  return typeof raw === 'string' && VALID_POSITION_VIEW_MODES.has(raw as PositionViewMode)
    ? (raw as PositionViewMode)
    : fallback;
}

function readMarketSectionViewMode(raw: unknown, fallback: MarketSectionViewMode): MarketSectionViewMode {
  return typeof raw === 'string' && VALID_MARKET_SECTION_VIEW_MODES.has(raw as MarketSectionViewMode)
    ? (raw as MarketSectionViewMode)
    : fallback;
}

function readBotBoardViewMode(raw: unknown, fallback: BotBoardViewMode): BotBoardViewMode {
  return typeof raw === 'string' && VALID_BOT_BOARD_VIEW_MODES.has(raw as BotBoardViewMode)
    ? (raw as BotBoardViewMode)
    : fallback;
}

function readBotBoardSortKey(raw: unknown, fallback: BotBoardSortKey): BotBoardSortKey {
  return typeof raw === 'string' && VALID_BOT_BOARD_SORT_KEYS.has(raw as BotBoardSortKey)
    ? (raw as BotBoardSortKey)
    : fallback;
}

/** Shared by hydration and the setter so both paths agree: drop anything that
 *  isn't a current Sport rather than letting one bad entry poison the list
 *  (cf. savedGames.load), dedupe, and emit in canonical SPORT_TABS order so
 *  the persisted representation is stable regardless of input order. */
function normalizeHiddenEmptySports(raw: unknown): Sport[] {
  if (!Array.isArray(raw)) return [];
  const picked = new Set<Sport>();
  for (const entry of raw) {
    if (typeof entry === 'string' && VALID_SPORTS.has(entry as Sport)) {
      picked.add(entry as Sport);
    }
  }
  return SPORT_TABS.map((t) => t.id).filter((sport) => picked.has(sport));
}

/** Shared by hydration and the setter: keep whole contracts ≥ 1, dedupe
 *  preserving first-seen order (chips render in the order the trader entered
 *  them), clamp to 3. Garbage entries drop individually rather than poisoning
 *  the list (cf. normalizeHiddenEmptySports). */
function normalizeQuickSizes(raw: unknown): number[] {
  if (!Array.isArray(raw)) return [];
  const picked: number[] = [];
  for (const entry of raw) {
    if (typeof entry !== 'number' || !Number.isFinite(entry)) continue;
    const n = Math.floor(entry);
    if (n >= 1 && !picked.includes(n)) picked.push(n);
    if (picked.length >= 3) break;
  }
  return picked;
}

function read(): PersistedShape {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Partial<PersistedShape> & {
      /** Pre-split legacy preference. Intentionally not written anymore. */
      viewMode?: unknown;
    };
    const rawContracts = typeof parsed.defaultContracts === 'number'
      ? Math.floor(parsed.defaultContracts)
      : DEFAULTS.defaultContracts;
    return {
      feeAdjustedOdds: parsed.feeAdjustedOdds ?? DEFAULTS.feeAdjustedOdds,
      hideUnknownFeePrices:
        typeof parsed.hideUnknownFeePrices === 'boolean'
          ? parsed.hideUnknownFeePrices
          : DEFAULTS.hideUnknownFeePrices,
      makerRebateEnabled:
        typeof parsed.makerRebateEnabled === 'boolean'
          ? parsed.makerRebateEnabled
          : DEFAULTS.makerRebateEnabled,
      defaultSport: parsed.defaultSport && VALID_SPORTS.has(parsed.defaultSport)
        ? parsed.defaultSport
        : DEFAULTS.defaultSport,
      defaultMarketType:
        parsed.defaultMarketType && VALID_MARKET_TYPES.has(parsed.defaultMarketType)
          ? parsed.defaultMarketType
          : DEFAULTS.defaultMarketType,
      defaultPriceMode:
        parsed.defaultPriceMode && VALID_PRICE_MODES.has(parsed.defaultPriceMode)
          ? parsed.defaultPriceMode
          : DEFAULTS.defaultPriceMode,
      bookDepthLevels:
        typeof parsed.bookDepthLevels === 'number' &&
        Number.isFinite(parsed.bookDepthLevels) &&
        parsed.bookDepthLevels >= 1 &&
        parsed.bookDepthLevels <= 20
          ? Math.floor(parsed.bookDepthLevels)
          : DEFAULTS.bookDepthLevels,
      defaultExpiryMode:
        parsed.defaultExpiryMode && VALID_EXPIRY_MODES.has(parsed.defaultExpiryMode)
          ? parsed.defaultExpiryMode
          : DEFAULTS.defaultExpiryMode,
      defaultPolyExpiryMode:
        parsed.defaultPolyExpiryMode && VALID_EXPIRY_MODES.has(parsed.defaultPolyExpiryMode)
          ? parsed.defaultPolyExpiryMode
          : DEFAULTS.defaultPolyExpiryMode,
      defaultPolyUsExpiryMode:
        parsed.defaultPolyUsExpiryMode && VALID_EXPIRY_MODES.has(parsed.defaultPolyUsExpiryMode)
          ? parsed.defaultPolyUsExpiryMode
          : DEFAULTS.defaultPolyUsExpiryMode,
      defaultContracts: Number.isFinite(rawContracts) && rawContracts >= 1
        ? rawContracts
        : DEFAULTS.defaultContracts,
      quickSizes: normalizeQuickSizes(parsed.quickSizes),
      defaultBlockerContracts: (() => {
        const raw = parsed.defaultBlockerContracts;
        if (typeof raw !== 'number' || !Number.isFinite(raw)) return DEFAULTS.defaultBlockerContracts;
        const n = Math.floor(raw);
        return n >= 1 ? n : DEFAULTS.defaultBlockerContracts;
      })(),
      oscarBlockerDefaultOn:
        typeof parsed.oscarBlockerDefaultOn === 'boolean'
          ? parsed.oscarBlockerDefaultOn
          : DEFAULTS.oscarBlockerDefaultOn,
      positionViewMode: readPositionViewMode(
        parsed.positionViewMode,
        DEFAULTS.positionViewMode,
      ),
      orderViewMode: readSurfaceViewMode(
        parsed.orderViewMode,
        DEFAULTS.orderViewMode,
      ),
      tradeViewMode: readSurfaceViewMode(
        parsed.tradeViewMode,
        DEFAULTS.tradeViewMode,
      ),
      spreadTotalViewMode: readMarketSectionViewMode(
        parsed.spreadTotalViewMode,
        DEFAULTS.spreadTotalViewMode,
      ),
      moneylineViewMode: readMarketSectionViewMode(
        parsed.moneylineViewMode,
        DEFAULTS.moneylineViewMode,
      ),
      gameLinesViewMode: readMarketSectionViewMode(
        parsed.gameLinesViewMode,
        DEFAULTS.gameLinesViewMode,
      ),
      defaultDashboardSort: readDashboardSort(parsed.defaultDashboardSort),
      defaultPositionsOrderSort: readPositionsOrderSort(parsed.defaultPositionsOrderSort),
      botFleetViewMode: readBotBoardViewMode(
        parsed.botFleetViewMode,
        DEFAULTS.botFleetViewMode,
      ),
      myBotsViewMode: readBotBoardViewMode(
        parsed.myBotsViewMode,
        DEFAULTS.myBotsViewMode,
      ),
      orgBotsViewMode: readBotBoardViewMode(
        parsed.orgBotsViewMode,
        DEFAULTS.orgBotsViewMode,
      ),
      botFleetSort: readBotBoardSortKey(parsed.botFleetSort, DEFAULTS.botFleetSort),
      myBotsSort: readBotBoardSortKey(parsed.myBotsSort, DEFAULTS.myBotsSort),
      orgBotsSort: readBotBoardSortKey(parsed.orgBotsSort, DEFAULTS.orgBotsSort),
      confirmOrderCancel:
        typeof parsed.confirmOrderCancel === 'boolean'
          ? parsed.confirmOrderCancel
          : DEFAULTS.confirmOrderCancel,
      gameCancelTickerFallback:
        typeof parsed.gameCancelTickerFallback === 'boolean'
          ? parsed.gameCancelTickerFallback
          : DEFAULTS.gameCancelTickerFallback,
      showPnl:
        typeof parsed.showPnl === 'boolean' ? parsed.showPnl : DEFAULTS.showPnl,
      showRestingCloseCount:
        typeof parsed.showRestingCloseCount === 'boolean' ? parsed.showRestingCloseCount : true,
      restingCloseCountOwnerId:
        typeof parsed.restingCloseCountOwnerId === 'string' && parsed.restingCloseCountOwnerId.length > 0
          ? parsed.restingCloseCountOwnerId : null,
      pinnedOrderTicket:
        typeof parsed.pinnedOrderTicket === 'boolean'
          ? parsed.pinnedOrderTicket
          : DEFAULTS.pinnedOrderTicket,
      showOpOdds:
        typeof parsed.showOpOdds === 'boolean' ? parsed.showOpOdds : DEFAULTS.showOpOdds,
      // `showOoOdds` (OpticOdds trial column, removed 2026-09-06) and
      // `showUnbtdOdds` (Unabated column, retired with the channel #1192)
      // may still sit in persisted settings — ignored, not an error; the
      // next write drops them.
      hiddenEmptySports: normalizeHiddenEmptySports(parsed.hiddenEmptySports),
      kalshiMarketsOnly:
        typeof parsed.kalshiMarketsOnly === 'boolean'
          ? parsed.kalshiMarketsOnly
          : DEFAULTS.kalshiMarketsOnly,
      cfbSavedOnly:
        typeof parsed.cfbSavedOnly === 'boolean'
          ? parsed.cfbSavedOnly
          : DEFAULTS.cfbSavedOnly,
      lt2HotkeysEnabled:
        typeof parsed.lt2HotkeysEnabled === 'boolean'
          ? parsed.lt2HotkeysEnabled
          : DEFAULTS.lt2HotkeysEnabled,
      lt2PullAllEnabled:
        typeof parsed.lt2PullAllEnabled === 'boolean'
          ? parsed.lt2PullAllEnabled
          : DEFAULTS.lt2PullAllEnabled,
      lt2TwoSidedEnabled:
        typeof parsed.lt2TwoSidedEnabled === 'boolean'
          ? parsed.lt2TwoSidedEnabled
          : DEFAULTS.lt2TwoSidedEnabled,
      lt2TwoSidedOwnerId:
        typeof parsed.lt2TwoSidedOwnerId === 'string' && parsed.lt2TwoSidedOwnerId !== ''
          ? parsed.lt2TwoSidedOwnerId
          : DEFAULTS.lt2TwoSidedOwnerId,
      // Both or neither: a multiplier with no owner counts for nobody, so it
      // is dropped rather than kept as an orphan a later owner could inherit.
      ...readTwoSidedMultiplier(parsed),
      spreadTotalShowPolyOnlyLines:
        typeof parsed.spreadTotalShowPolyOnlyLines === 'boolean'
          ? parsed.spreadTotalShowPolyOnlyLines
          : DEFAULTS.spreadTotalShowPolyOnlyLines,
      spreadTotalNearestLines:
        typeof parsed.spreadTotalNearestLines === 'boolean'
          ? parsed.spreadTotalNearestLines
          : DEFAULTS.spreadTotalNearestLines,
      // An unknown stored value lands on `compact`, not on the last-known one:
      // the safe presentation is the bounded-by-default one, and a corrupt
      // value must not be able to select the roomier layout.
      restingOrderDisplay:
        typeof parsed.restingOrderDisplay === 'string' &&
        VALID_RESTING_ORDER_DISPLAYS.has(parsed.restingOrderDisplay as RestingOrderDisplay)
          ? (parsed.restingOrderDisplay as RestingOrderDisplay)
          : DEFAULTS.restingOrderDisplay,
      // Absent, old or unknown stored data lands on `day` — the factory view.
      // A corrupt value must never be able to select the wider window: #712
      // requires existing users to keep today's behavior until they opt in.
      slateRange:
        typeof parsed.slateRange === 'string' &&
        VALID_SLATE_RANGES.has(parsed.slateRange as SlateRange)
          ? (parsed.slateRange as SlateRange)
          : DEFAULTS.slateRange,
    };
  } catch {
    return { ...DEFAULTS };
  }
}

/**
 * #724 — a tab running an OLDER build rewrites this whole blob from its own
 * field list. A build from before S omits `lt2TwoSidedEnabled`; the September
 * 22 build omits `lt2TwoSidedOwnerId`. Read as-is, either write silently turns
 * S off in every newer tab (the missing field becomes the default), with no
 * refusal at the key, while the trader believes S is on.
 *
 * A missing key is an older writer, not a decision: keep what this tab holds
 * for it. An explicit value is always honoured, so any build can still turn S
 * off. Mutates `next`; returns whether anything was kept.
 */
export function keepTwoSidedAcrossOlderWriter(
  rawPayload: string | null,
  current: Pick<
    PersistedShape,
    | 'lt2TwoSidedEnabled'
    | 'lt2TwoSidedOwnerId'
    | 'lt2TwoSidedMultiplier'
    | 'lt2TwoSidedMultiplierOwnerId'
  >,
  next: PersistedShape,
): boolean {
  let parsed: Record<string, unknown> | null = null;
  try {
    const value: unknown = rawPayload == null ? null : JSON.parse(rawPayload);
    if (value != null && typeof value === 'object' && !Array.isArray(value)) {
      parsed = value as Record<string, unknown>;
    }
  } catch {
    return false;
  }
  if (parsed == null) return false;
  const has = (key: string) => Object.prototype.hasOwnProperty.call(parsed, key);
  // #1067 — every build before Shift+S omits the multiplier pair. Kept on its
  // own, before the S checks below return, since an older writer can drop the
  // multiplier while keeping S.
  let keptMultiplier = false;
  if (!has('lt2TwoSidedMultiplier') && current.lt2TwoSidedMultiplier != null) {
    next.lt2TwoSidedMultiplier = current.lt2TwoSidedMultiplier;
    next.lt2TwoSidedMultiplierOwnerId = current.lt2TwoSidedMultiplierOwnerId;
    keptMultiplier = true;
  }
  if (!has('lt2TwoSidedEnabled')) {
    if (!current.lt2TwoSidedEnabled && current.lt2TwoSidedOwnerId == null) return keptMultiplier;
    next.lt2TwoSidedEnabled = current.lt2TwoSidedEnabled;
    next.lt2TwoSidedOwnerId = current.lt2TwoSidedOwnerId;
    return true;
  }
  if (next.lt2TwoSidedEnabled && !has('lt2TwoSidedOwnerId') && current.lt2TwoSidedOwnerId != null) {
    next.lt2TwoSidedOwnerId = current.lt2TwoSidedOwnerId;
    return true;
  }
  return keptMultiplier;
}

/** Mirrors `ONE_CLICK_MAX_CONTRACTS` (oneClickStore), the largest ceiling any
 *  trader can hold; literal here to keep this store free of that import. */
const MAX_TWO_SIDED_MULTIPLIER = 2_147_483_647;

/** #1067 — the only multiplier values that exist: whole numbers ≥2. One is
 *  plain S; a fraction would make a fractional contract count. Capped at the
 *  largest possible one-click ceiling: anything above it could never pass
 *  the ceiling even at a one-contract base, so it is not a real choice. */
export function isValidTwoSidedMultiplier(n: unknown): n is number {
  return typeof n === 'number' && Number.isSafeInteger(n) && n >= 2 && n <= MAX_TWO_SIDED_MULTIPLIER;
}

function readTwoSidedMultiplier(
  parsed: { lt2TwoSidedMultiplier?: unknown; lt2TwoSidedMultiplierOwnerId?: unknown },
): Pick<PersistedShape, 'lt2TwoSidedMultiplier' | 'lt2TwoSidedMultiplierOwnerId'> {
  const n = parsed.lt2TwoSidedMultiplier;
  const owner = parsed.lt2TwoSidedMultiplierOwnerId;
  return isValidTwoSidedMultiplier(n) && typeof owner === 'string' && owner !== ''
    ? { lt2TwoSidedMultiplier: n, lt2TwoSidedMultiplierOwnerId: owner }
    : { lt2TwoSidedMultiplier: null, lt2TwoSidedMultiplierOwnerId: null };
}

function write(s: PersistedShape) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
  } catch {
    // localStorage full or unavailable — silent
  }
}

interface DisplaySettingsStore extends PersistedShape {
  toggleFeeAdjustedOdds: () => void;
  toggleHideUnknownFeePrices: () => void;
  toggleMakerRebate: () => void;
  setDefaultSport: (sport: Sport) => void;
  setDefaultMarketType: (mt: DashboardMarketType) => void;
  setDefaultPriceMode: (pm: PriceMode) => void;
  setBookDepthLevels: (levels: number) => void;
  setDefaultExpiryMode: (mode: DefaultExpiryMode) => void;
  setDefaultPolyExpiryMode: (mode: DefaultExpiryMode) => void;
  setDefaultPolyUsExpiryMode: (mode: DefaultExpiryMode) => void;
  setDefaultContracts: (n: number) => void;
  setQuickSizes: (sizes: number[]) => void;
  setDefaultBlockerContracts: (n: number | null) => void;
  toggleOscarBlockerDefaultOn: () => void;
  setPositionViewMode: (m: PositionViewMode) => void;
  setOrderViewMode: (m: SurfaceViewMode) => void;
  setTradeViewMode: (m: SurfaceViewMode) => void;
  setSpreadTotalViewMode: (m: MarketSectionViewMode) => void;
  setGameLinesViewMode: (m: MarketSectionViewMode) => void;
  setMoneylineViewMode: (m: MarketSectionViewMode) => void;
  setDefaultDashboardSort: (sort: DashboardSortDefault) => void;
  setDefaultPositionsOrderSort: (sort: PositionsOrderSortDefault) => void;
  setBotFleetViewMode: (m: BotBoardViewMode) => void;
  setMyBotsViewMode: (m: BotBoardViewMode) => void;
  setOrgBotsViewMode: (m: BotBoardViewMode) => void;
  setBotFleetSort: (key: BotBoardSortKey) => void;
  setMyBotsSort: (key: BotBoardSortKey) => void;
  setOrgBotsSort: (key: BotBoardSortKey) => void;
  setConfirmOrderCancel: (enabled: boolean) => void;
  setGameCancelTickerFallback: (enabled: boolean) => void;
  toggleShowPnl: () => void;
  setShowRestingCloseCount: (visible: boolean, ownerId: string | null) => void;
  setPinnedOrderTicket: (enabled: boolean) => void;
  setShowOpOdds: (enabled: boolean) => void;
  setHiddenEmptySports: (sports: Sport[]) => void;
  setKalshiMarketsOnly: (enabled: boolean) => void;
  setCfbSavedOnly: (enabled: boolean) => void;
  setLt2HotkeysEnabled: (enabled: boolean) => void;
  setLt2PullAllEnabled: (enabled: boolean) => void;
  /** Turn S on for `ownerId` (the signed-in trader), or off for everyone. */
  setLt2TwoSidedEnabled: (enabled: boolean, ownerId: string | null) => void;
  /** #1067 — set the Shift+S multiplier for `ownerId`, or clear it (null).
   *  Anything but a whole number ≥2 is refused, not coerced. */
  setLt2TwoSidedMultiplier: (multiplier: number | null, ownerId: string | null) => void;
  setSpreadTotalShowPolyOnlyLines: (enabled: boolean) => void;
  setSpreadTotalNearestLines: (enabled: boolean) => void;
  setRestingOrderDisplay: (mode: RestingOrderDisplay) => void;
  setSlateRange: (range: SlateRange) => void;
}

function snapshot(s: PersistedShape): PersistedShape {
  return {
    feeAdjustedOdds: s.feeAdjustedOdds,
    hideUnknownFeePrices: s.hideUnknownFeePrices,
    makerRebateEnabled: s.makerRebateEnabled,
    defaultSport: s.defaultSport,
    defaultMarketType: s.defaultMarketType,
    defaultPriceMode: s.defaultPriceMode,
    bookDepthLevels: s.bookDepthLevels,
    defaultExpiryMode: s.defaultExpiryMode,
    defaultPolyExpiryMode: s.defaultPolyExpiryMode,
    defaultPolyUsExpiryMode: s.defaultPolyUsExpiryMode,
    defaultContracts: s.defaultContracts,
    quickSizes: s.quickSizes,
    defaultBlockerContracts: s.defaultBlockerContracts,
    oscarBlockerDefaultOn: s.oscarBlockerDefaultOn,
    positionViewMode: s.positionViewMode,
    orderViewMode: s.orderViewMode,
    tradeViewMode: s.tradeViewMode,
    spreadTotalViewMode: s.spreadTotalViewMode,
    gameLinesViewMode: s.gameLinesViewMode,
    moneylineViewMode: s.moneylineViewMode,
    defaultDashboardSort: s.defaultDashboardSort,
    defaultPositionsOrderSort: s.defaultPositionsOrderSort,
    botFleetViewMode: s.botFleetViewMode,
    myBotsViewMode: s.myBotsViewMode,
    orgBotsViewMode: s.orgBotsViewMode,
    botFleetSort: s.botFleetSort,
    myBotsSort: s.myBotsSort,
    orgBotsSort: s.orgBotsSort,
    confirmOrderCancel: s.confirmOrderCancel,
    gameCancelTickerFallback: s.gameCancelTickerFallback,
    showPnl: s.showPnl,
    showRestingCloseCount: s.showRestingCloseCount,
    restingCloseCountOwnerId: s.restingCloseCountOwnerId,
    pinnedOrderTicket: s.pinnedOrderTicket,
    showOpOdds: s.showOpOdds,
    hiddenEmptySports: s.hiddenEmptySports,
    kalshiMarketsOnly: s.kalshiMarketsOnly,
    cfbSavedOnly: s.cfbSavedOnly,
    lt2HotkeysEnabled: s.lt2HotkeysEnabled,
    lt2PullAllEnabled: s.lt2PullAllEnabled,
    lt2TwoSidedEnabled: s.lt2TwoSidedEnabled,
    lt2TwoSidedOwnerId: s.lt2TwoSidedOwnerId,
    lt2TwoSidedMultiplier: s.lt2TwoSidedMultiplier,
    lt2TwoSidedMultiplierOwnerId: s.lt2TwoSidedMultiplierOwnerId,
    spreadTotalShowPolyOnlyLines: s.spreadTotalShowPolyOnlyLines,
    spreadTotalNearestLines: s.spreadTotalNearestLines,
    restingOrderDisplay: s.restingOrderDisplay,
    slateRange: s.slateRange,
  };
}

export const useDisplaySettings = create<DisplaySettingsStore>((set, get) => ({
  ...read(),
  toggleFeeAdjustedOdds: () => {
    set((s) => ({ feeAdjustedOdds: !s.feeAdjustedOdds }));
    write(snapshot(get()));
  },
  toggleHideUnknownFeePrices: () => {
    set((s) => ({ hideUnknownFeePrices: !s.hideUnknownFeePrices }));
    write(snapshot(get()));
  },
  toggleMakerRebate: () => {
    set((s) => ({ makerRebateEnabled: !s.makerRebateEnabled }));
    write(snapshot(get()));
  },
  toggleShowPnl: () => {
    set((s) => ({ showPnl: !s.showPnl }));
    write(snapshot(get()));
  },
  setShowRestingCloseCount: (visible, ownerId) => {
    if (!ownerId) return;
    // A different account choosing Show must not erase the owner's saved choice.
    const owner = get().restingCloseCountOwnerId;
    if (visible && owner != null && owner !== ownerId) return;
    set({ showRestingCloseCount: visible, restingCloseCountOwnerId: visible ? null : ownerId });
    write(snapshot(get()));
  },
  setDefaultSport: (sport) => {
    set({ defaultSport: sport });
    write(snapshot(get()));
  },
  setDefaultMarketType: (mt) => {
    set({ defaultMarketType: mt });
    write(snapshot(get()));
  },
  setDefaultPriceMode: (pm) => {
    set({ defaultPriceMode: pm });
    write(snapshot(get()));
  },
  setBookDepthLevels: (levels) => {
    const clean =
      Number.isFinite(levels) && levels >= 1 && levels <= 20 ? Math.floor(levels) : DEFAULTS.bookDepthLevels;
    set({ bookDepthLevels: clean });
    write(snapshot(get()));
  },
  setDefaultExpiryMode: (mode) => {
    set({ defaultExpiryMode: mode });
    write(snapshot(get()));
  },
  setDefaultPolyExpiryMode: (mode) => {
    set({ defaultPolyExpiryMode: mode });
    write(snapshot(get()));
  },
  setDefaultPolyUsExpiryMode: (mode) => {
    set({ defaultPolyUsExpiryMode: mode });
    write(snapshot(get()));
  },
  setDefaultContracts: (n) => {
    const clean = Number.isFinite(n) && n >= 1 ? Math.floor(n) : DEFAULTS.defaultContracts;
    set({ defaultContracts: clean });
    write(snapshot(get()));
  },
  setQuickSizes: (sizes) => {
    set({ quickSizes: normalizeQuickSizes(sizes) });
    write(snapshot(get()));
  },
  setDefaultBlockerContracts: (n) => {
    // null = follow defaultContracts; a number must be a whole ≥ 1.
    const clean = n == null || !Number.isFinite(n) || n < 1 ? null : Math.floor(n);
    set({ defaultBlockerContracts: clean });
    write(snapshot(get()));
  },
  toggleOscarBlockerDefaultOn: () => {
    set((s) => ({ oscarBlockerDefaultOn: !s.oscarBlockerDefaultOn }));
    write(snapshot(get()));
  },
  setPositionViewMode: (m) => {
    set({
      positionViewMode: VALID_POSITION_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.positionViewMode,
    });
    write(snapshot(get()));
  },
  setOrderViewMode: (m) => {
    set({
      orderViewMode: VALID_SURFACE_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.orderViewMode,
    });
    write(snapshot(get()));
  },
  setTradeViewMode: (m) => {
    set({
      tradeViewMode: VALID_SURFACE_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.tradeViewMode,
    });
    write(snapshot(get()));
  },
  setSpreadTotalViewMode: (m) => {
    set({
      spreadTotalViewMode: VALID_MARKET_SECTION_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.spreadTotalViewMode,
    });
    write(snapshot(get()));
  },
  setGameLinesViewMode: (m) => {
    set({
      gameLinesViewMode: VALID_MARKET_SECTION_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.gameLinesViewMode,
    });
    write(snapshot(get()));
  },
  setMoneylineViewMode: (m) => {
    set({
      moneylineViewMode: VALID_MARKET_SECTION_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.moneylineViewMode,
    });
    write(snapshot(get()));
  },
  setDefaultDashboardSort: (sort) => {
    // Defensive normalization — if a caller hands us a key not in the
    // current sortable-columns list, fall back to factory default rather
    // than persisting nonsense that confuses the next read().
    const key =
      sort.key === null || VALID_DASHBOARD_SORT_KEYS.has(sort.key)
        ? sort.key
        : DEFAULTS.defaultDashboardSort.key;
    const dir = VALID_SORT_DIRS.has(sort.dir)
      ? sort.dir
      : DEFAULTS.defaultDashboardSort.dir;
    set({ defaultDashboardSort: { key, dir } });
    write(snapshot(get()));
  },
  setDefaultPositionsOrderSort: (sort) => {
    const key = VALID_POSITIONS_ORDER_SORT_KEYS.has(sort.key)
      ? sort.key
      : DEFAULTS.defaultPositionsOrderSort.key;
    const dir = VALID_SORT_DIRS.has(sort.dir)
      ? sort.dir
      : DEFAULTS.defaultPositionsOrderSort.dir;
    set({ defaultPositionsOrderSort: { key, dir } });
    write(snapshot(get()));
  },
  setBotFleetViewMode: (m) => {
    set({
      botFleetViewMode: VALID_BOT_BOARD_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.botFleetViewMode,
    });
    write(snapshot(get()));
  },
  setMyBotsViewMode: (m) => {
    set({
      myBotsViewMode: VALID_BOT_BOARD_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.myBotsViewMode,
    });
    write(snapshot(get()));
  },
  setOrgBotsViewMode: (m) => {
    set({
      orgBotsViewMode: VALID_BOT_BOARD_VIEW_MODES.has(m)
        ? m
        : DEFAULTS.orgBotsViewMode,
    });
    write(snapshot(get()));
  },
  setBotFleetSort: (key) => {
    set({
      botFleetSort: VALID_BOT_BOARD_SORT_KEYS.has(key)
        ? key
        : DEFAULTS.botFleetSort,
    });
    write(snapshot(get()));
  },
  setMyBotsSort: (key) => {
    set({
      myBotsSort: VALID_BOT_BOARD_SORT_KEYS.has(key)
        ? key
        : DEFAULTS.myBotsSort,
    });
    write(snapshot(get()));
  },
  setOrgBotsSort: (key) => {
    set({
      orgBotsSort: VALID_BOT_BOARD_SORT_KEYS.has(key)
        ? key
        : DEFAULTS.orgBotsSort,
    });
    write(snapshot(get()));
  },
  setConfirmOrderCancel: (enabled) => {
    set({ confirmOrderCancel: enabled });
    write(snapshot(get()));
  },
  setGameCancelTickerFallback: (enabled) => {
    set({ gameCancelTickerFallback: enabled });
    write(snapshot(get()));
  },
  setPinnedOrderTicket: (enabled) => {
    set({ pinnedOrderTicket: enabled });
    write(snapshot(get()));
  },
  setShowOpOdds: (enabled) => {
    set({ showOpOdds: enabled });
    write(snapshot(get()));
  },
  setHiddenEmptySports: (sports) => {
    set({ hiddenEmptySports: normalizeHiddenEmptySports(sports) });
    write(snapshot(get()));
  },
  setKalshiMarketsOnly: (enabled) => {
    set({ kalshiMarketsOnly: enabled });
    write(snapshot(get()));
  },
  setCfbSavedOnly: (enabled) => {
    set({ cfbSavedOnly: enabled });
    write(snapshot(get()));
  },
  setLt2HotkeysEnabled: (enabled) => {
    set({ lt2HotkeysEnabled: enabled });
    write(snapshot(get()));
  },
  setLt2PullAllEnabled: (enabled) => {
    set({ lt2PullAllEnabled: enabled });
    write(snapshot(get()));
  },
  setLt2TwoSidedEnabled: (enabled, ownerId) => {
    // Off by someone who does not own the opt-in is already true for them
    // (they see Off); it must not switch S off for the trader who does.
    if (!enabled && get().lt2TwoSidedOwnerId != null && get().lt2TwoSidedOwnerId !== ownerId) {
      return;
    }
    const owner = enabled && ownerId != null && ownerId !== '' ? ownerId : null;
    set({ lt2TwoSidedEnabled: owner != null, lt2TwoSidedOwnerId: owner });
    write(snapshot(get()));
  },
  setLt2TwoSidedMultiplier: (multiplier, ownerId) => {
    const current = get().lt2TwoSidedMultiplierOwnerId;
    // Same owner rule as the S opt-in: clearing someone else's value is a
    // no-op (it already reads as unset for you), never a reset of theirs.
    if (multiplier == null) {
      if (current != null && current !== ownerId) return;
      set({ lt2TwoSidedMultiplier: null, lt2TwoSidedMultiplierOwnerId: null });
      write(snapshot(get()));
      return;
    }
    if (!isValidTwoSidedMultiplier(multiplier) || ownerId == null || ownerId === '') return;
    set({ lt2TwoSidedMultiplier: multiplier, lt2TwoSidedMultiplierOwnerId: ownerId });
    write(snapshot(get()));
  },
  setSpreadTotalShowPolyOnlyLines: (enabled) => {
    set({ spreadTotalShowPolyOnlyLines: enabled });
    write(snapshot(get()));
  },
  setSpreadTotalNearestLines: (enabled) => {
    set({ spreadTotalNearestLines: enabled });
    write(snapshot(get()));
  },
  setRestingOrderDisplay: (mode) => {
    set({ restingOrderDisplay: mode });
    write(snapshot(get()));
  },
  setSlateRange: (range) => {
    set({ slateRange: range });
    write(snapshot(get()));
  },
}));

// Cross-tab sync. Each tab keeps its own in-memory copy; without this, a tab
// that changed one field later writes its WHOLE (now-stale) snapshot and clobbers
// a sibling tab's change — the "settings reset on refresh" traders report with
// two tabs/windows open. The `storage` event fires only in OTHER tabs (never the
// writer), so re-hydrating from localStorage here can't loop, and setState merges
// over the existing setters. A real localStorage.clear() fires with key===null
// (ignored by the guard below), so only a direct write to STORAGE_KEY triggers a
// re-read — and removeItem of this key never happens in app code.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === STORAGE_KEY) {
      const next = read();
      const kept = keepTwoSidedAcrossOlderWriter(e.newValue, useDisplaySettings.getState(), next);
      useDisplaySettings.setState(next);
      // Put back what an older build dropped, so the next tab to load reads it.
      // Storage events never fire in the writer, so this cannot loop.
      if (kept) write(snapshot(useDisplaySettings.getState()));
    }
  });
}
